import type {
  EstateStatus,
  EstateStatusAttachmentLink,
  EstateStatusChecklistItem,
} from '../../../../EstateStatus/types';
import type { File as GQLFile } from '../../../../generated/graphql';
import { EstateStatusItemState } from '../../../../generated/graphql';
import {
  cloneEstateStatusChecklist,
  buildEstateStatusAttachmentSaveInput,
  buildEstateStatusChecklistSaveInput,
  calculateEstateStatusSummary,
  ESTATE_STATUS_AUTOSAVE_DEBOUNCE_MS,
  ESTATE_STATUS_PERCENT_SCALE,
  formatEstateStatusWeight,
  getEstateStatusDefaultHouseholdChecklist,
  isEstateStatusAttachmentSavable,
  normalizeEstateStatusDateTime,
  useEstateStatusHouseholdSettings,
  useSaveEstateStatusHouseholdSettings,
} from '../../../../EstateStatus';
import { useFeatureFlagsContext } from '../../../../FeatureFlags';
import { useSelf } from '../../../../User';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEstateStatusAutosave } from '../../../../EstateStatus/hooks/useEstateStatusAutosave';

const estateStatusStates: EstateStatusItemState[] = [
  EstateStatusItemState.Yes,
  EstateStatusItemState.No,
  EstateStatusItemState.Na,
];

export interface EstateStatusDetailViewIndexPageHookArgs {
  firmID: string;
  householdID: string;
  onChecklistSaved?: (savedEstateStatus: EstateStatus) => void;
}

function mapUploadedFileToAttachmentLink(
  file: GQLFile,
  uploadedByUserID: string | null | undefined
): EstateStatusAttachmentLink {
  return {
    id: file.id,
    fileName: file.name,
    url: file.downloadURL ?? '',
    uploadedAt: file.createdAt,
    uploadedByUserID: uploadedByUserID ?? '',
  };
}

function buildAttachmentDirtyComparisonInput(attachmentLinks: EstateStatusAttachmentLink[]) {
  return buildEstateStatusAttachmentSaveInput(attachmentLinks).map(({ id, fileName }) => ({ id, fileName }));
}

function buildUnsavableAttachmentKeys(attachmentLinks: EstateStatusAttachmentLink[]) {
  return attachmentLinks
    .filter((attachmentLink) => !isEstateStatusAttachmentSavable(attachmentLink))
    .map((attachmentLink) =>
      JSON.stringify([attachmentLink.id ?? null, attachmentLink.fileName.trim(), attachmentLink.url.trim()])
    );
}

function serializeChecklistForDirtyComparison(checklistToCompare: EstateStatusChecklistItem[]) {
  return JSON.stringify(
    checklistToCompare.map((item) => ({
      id: item.id,
      state: item.state,
      lastReviewedAt: normalizeEstateStatusDateTime(item.lastReviewedAt),
      note: item.note ?? '',
      attachmentLinks: buildAttachmentDirtyComparisonInput(item.attachmentLinks),
    }))
  );
}

function hasRemovedUnsavableAttachment(
  checklistToSave: EstateStatusChecklistItem[],
  checklistToCompare: EstateStatusChecklistItem[]
) {
  return checklistToCompare.some((syncedItem) => {
    const currentItem = checklistToSave.find((item) => item.id === syncedItem.id);
    const currentAttachmentKeys = new Set(buildUnsavableAttachmentKeys(currentItem?.attachmentLinks ?? []));

    return buildUnsavableAttachmentKeys(syncedItem.attachmentLinks).some(
      (attachmentKey) => !currentAttachmentKeys.has(attachmentKey)
    );
  });
}

function hasChecklistChanges(
  checklistToSave: EstateStatusChecklistItem[],
  checklistToCompare: EstateStatusChecklistItem[]
) {
  return (
    serializeChecklistForDirtyComparison(checklistToSave) !==
      serializeChecklistForDirtyComparison(checklistToCompare) ||
    hasRemovedUnsavableAttachment(checklistToSave, checklistToCompare)
  );
}

export function useEstateStatusElementDetailViewIndexPage({
  firmID,
  householdID,
  onChecklistSaved,
}: EstateStatusDetailViewIndexPageHookArgs) {
  const { t } = useTranslation('estateStatus');
  const { allowEstateStatus, allowTargetScoreRanges } = useFeatureFlagsContext();
  const { self } = useSelf();
  const estateStatusHouseholdID = allowEstateStatus === true ? householdID : undefined;
  const { estateStatusHouseholdSettings, estateStatusHouseholdSettingsQuery } =
    useEstateStatusHouseholdSettings(estateStatusHouseholdID);
  const { saveEstateStatusHouseholdSettingsMutation } = useSaveEstateStatusHouseholdSettings(
    estateStatusHouseholdID,
    firmID
  );
  const resetSaveEstateStatusHouseholdSettings = saveEstateStatusHouseholdSettingsMutation.reset;
  const [checklist, setChecklist] = useState<EstateStatusChecklistItem[]>([]);
  const [syncedChecklist, setSyncedChecklist] = useState<EstateStatusChecklistItem[]>([]);
  const [estateStatusChangeToken, setEstateStatusChangeToken] = useState('');
  const [expandedItemIDs, setExpandedItemIDs] = useState<string[]>([]);

  const hasLoadedHouseholdChecklist = (estateStatusHouseholdSettings?.checklist?.length ?? 0) > 0;
  const shouldHydrateInitialChecklist =
    hasLoadedHouseholdChecklist ||
    (!estateStatusHouseholdSettingsQuery.isLoading && !estateStatusHouseholdSettingsQuery.isError);

  const summary = calculateEstateStatusSummary(checklist);
  const isDirty = hasChecklistChanges(checklist, syncedChecklist);
  const isSaving = saveEstateStatusHouseholdSettingsMutation.isLoading;
  const canAutoSaveChecklist = (
    checklistToSave: EstateStatusChecklistItem[],
    checklistToCompare: EstateStatusChecklistItem[]
  ) =>
    allowEstateStatus === true &&
    checklistToSave.length > 0 &&
    !estateStatusHouseholdSettingsQuery.isLoading &&
    !estateStatusHouseholdSettingsQuery.isError &&
    hasChecklistChanges(checklistToSave, checklistToCompare);
  const autosave = useEstateStatusAutosave({
    value: checklist,
    syncedValue: syncedChecklist,
    changeToken: estateStatusChangeToken,
    isDirty,
    isMutationSaving: isSaving,
    debounceMs: ESTATE_STATUS_AUTOSAVE_DEBOUNCE_MS,
    debounceDependencies: [isSaving, estateStatusChangeToken, checklist],
    canSaveValue: canAutoSaveChecklist,
    serializeValue: serializeChecklistForDirtyComparison,
    saveValue: ({ changeToken, value }) =>
      saveEstateStatusHouseholdSettingsMutation.mutateAsync({
        changeToken,
        checklist: buildEstateStatusChecklistSaveInput(value),
      }),
    savedValueToSyncedValue: (savedEstateStatus) => cloneEstateStatusChecklist(savedEstateStatus.checklist),
    savedValueToChangeToken: (savedEstateStatus) => savedEstateStatus.changeToken,
    onSavedValue: ({ savedValue, savedSyncedValue, savedChangeToken, isMounted }) => {
      if (isMounted) {
        setEstateStatusChangeToken(savedChangeToken);
        setSyncedChecklist(savedSyncedValue);
      }

      onChecklistSaved?.(savedValue);
    },
  });

  useEffect(() => {
    // Avoid flashing the default template before the real household query settles.
    if (!shouldHydrateInitialChecklist) {
      return;
    }

    const sourceChecklist =
      estateStatusHouseholdSettings?.checklist.length && estateStatusHouseholdSettings.checklist.length > 0
        ? estateStatusHouseholdSettings.checklist
        : getEstateStatusDefaultHouseholdChecklist((key) => t(key));
    const nextChecklist = cloneEstateStatusChecklist(sourceChecklist);
    const nextChangeToken = estateStatusHouseholdSettings?.changeToken ?? '';

    setEstateStatusChangeToken(nextChangeToken);
    autosave.changeTokenRef.current = nextChangeToken;
    setSyncedChecklist(nextChecklist);
    autosave.syncedValueRef.current = nextChecklist;
    setChecklist((currentChecklist) =>
      currentChecklist.length === 0 || !autosave.isDirtyRef.current ? nextChecklist : currentChecklist
    );
    if (!autosave.isDirtyRef.current) {
      autosave.currentValueRef.current = nextChecklist;
      resetSaveEstateStatusHouseholdSettings();
    }
  }, [
    autosave.changeTokenRef,
    autosave.currentValueRef,
    autosave.isDirtyRef,
    autosave.syncedValueRef,
    estateStatusHouseholdSettings,
    resetSaveEstateStatusHouseholdSettings,
    shouldHydrateInitialChecklist,
    t,
  ]);

  const hasBlockingError = checklist.length === 0 && estateStatusHouseholdSettingsQuery.isError;
  const isEstateStatusDisabled = Boolean(firmID) && allowEstateStatus === false;
  const applicableItemLabel = t(summary.applicable === 1 ? 'item-singular' : 'item-plural');
  const stateLabels: Record<EstateStatusItemState, string> = {
    [EstateStatusItemState.Yes]: t('state-yes'),
    [EstateStatusItemState.No]: t('state-no'),
    [EstateStatusItemState.Na]: t('state-na'),
  };
  const completedLabel =
    summary.applicable === 0
      ? t('no-applicable-items')
      : t('completed-label', {
          completed: summary.completed,
          applicable: summary.applicable,
          itemLabel: applicableItemLabel,
        });
  const excludedCount = Math.max(checklist.length - summary.applicable, 0);
  const estateStatusScorecardElementValue = summary.score == null ? null : summary.score / ESTATE_STATUS_PERCENT_SCALE;
  const formatEstateStatusPercent = (score: number | null | undefined) =>
    score == null ? t('not-applicable') : formatEstateStatusWeight(score);
  const mathLabel =
    summary.score == null
      ? t('not-applicable')
      : t('math-label', {
          earnedWeight: formatEstateStatusWeight(summary.earnedWeight),
          applicableWeight: formatEstateStatusWeight(summary.applicableWeight),
          score: formatEstateStatusPercent(summary.score ?? null),
        });
  const getAttachmentLabel = (attachmentCount: number) =>
    t(attachmentCount === 1 ? 'details-attachment-singular' : 'details-attachment-plural', {
      count: attachmentCount,
    });
  const getItemDetailsLabel = (item: EstateStatusChecklistItem) => {
    const hasNote = !!item.note?.trim();
    const attachmentCount = item.attachmentLinks.length;

    if (!hasNote && attachmentCount === 0) return t('details-add');
    if (hasNote && attachmentCount === 0) return t('details-note');
    if (!hasNote) return getAttachmentLabel(attachmentCount);

    return t('details-note-and-attachments', {
      attachmentLabel: getAttachmentLabel(attachmentCount),
    });
  };

  const updateChecklistItem = (
    itemID: string,
    updater: (item: EstateStatusChecklistItem) => EstateStatusChecklistItem
  ) => {
    if (saveEstateStatusHouseholdSettingsMutation.isError) {
      saveEstateStatusHouseholdSettingsMutation.reset();
    }

    // Mark local edits synchronously so the hydration effect cannot immediately
    // overwrite an optimistic checklist change before React flushes `isDirty`.
    setChecklist((currentChecklist) => {
      const nextChecklist = currentChecklist.map((item) => (item.id === itemID ? updater(item) : item));
      autosave.markDirty(nextChecklist);

      return nextChecklist;
    });
  };

  const toggleExpandedItem = (itemID: string) => {
    setExpandedItemIDs((currentExpandedItemIDs) =>
      currentExpandedItemIDs.includes(itemID)
        ? currentExpandedItemIDs.filter((currentItemID) => currentItemID !== itemID)
        : [...currentExpandedItemIDs, itemID]
    );
  };

  return {
    allowTargetScoreRanges,
    checklist,
    completedLabel,
    estateStatusScorecardElementValue,
    estateStatusStates,
    excludedCount,
    expandedItemIDs,
    hasBlockingError,
    isEstateStatusDisabled,
    isLoadingEstateStatus: estateStatusHouseholdSettingsQuery.isLoading && checklist.length === 0,
    mathLabel,
    saveEstateStatusHouseholdSettingsMutation,
    stateLabels,
    summary,
    formatEstateStatusPercent,
    getItemDetailsLabel,
    toggleExpandedItem,
    updateChecklistItem,
    saveCurrentChecklist: autosave.saveCurrentAsync,
    mapUploadedFileToAttachmentLink: (file: GQLFile) => mapUploadedFileToAttachmentLink(file, self?.id),
  };
}
